import { useEffect } from 'react';

interface ModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}

export default function Modal({ open, title, subtitle, onClose, children, maxWidth = 'max-w-lg' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true"></div>

      <div
        className={`relative w-full ${maxWidth} max-h-[92vh] bg-background-50 rounded-2xl border border-background-200 shadow-xl flex flex-col overflow-hidden animate-fade-in`}
      >
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 py-5 sm:py-6 border-b border-background-200/80 shrink-0">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-xl text-foreground-950 tracking-tight">{title}</h3>
            {subtitle && <p className="mt-1 text-sm text-foreground-500 leading-normal">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-xl bg-background-100 text-foreground-500 hover:text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="overflow-y-auto custom-scrollbar px-6 sm:px-8 py-6">{children}</div>
      </div>
    </div>
  );
}